<template>
  <div class="room-availability-picker">
    <div class="room-availability-picker__head">
      <h6 class="room-availability-picker__title">Select rooms</h6>
      <button
        v-if="addingNewRoomVisible"
        type="button"
        class="room-availability-picker__btn room-availability-picker__btn--muted"
        @click="cancelAddingRoom"
      >
        Cancel
      </button>
      <button
        type="button"
        class="room-availability-picker__btn room-availability-picker__btn--ghost"
        @click="$emit('close')"
      >
        Close
      </button>
    </div>

    <div class="room-availability-picker__filters">
      <label class="rap-filter-label">From</label>
      <label class="rap-filter-label">To</label>
      <label class="rap-filter-label">Status</label>
      <label class="rap-filter-label">Room type</label>
      <span class="rap-filter-label rap-filter-label--action" aria-hidden="true"></span>
      <label class="rap-filter-label">Search</label>

      <div class="room-availability-picker__filter">
        <DatePicker
          compact
          :value="Filter.start"
          placeholder="From"
          @input="setStartDate"
        />
      </div>
      <div class="room-availability-picker__filter">
        <DatePicker
          compact
          :value="Filter.end"
          placeholder="To"
          :min-date="endDatevalidator"
          @input="setEndDate"
        />
      </div>
      <div class="room-availability-picker__filter">
        <Multiselect
          v-model="status"
          :options="StatusTypes"
          placeholder="Status"
          label="Value"
          track-by="name"
          selectLabel=""
        >
          <template #singleLabel="{ option }">{{ option.Value }}</template>
        </Multiselect>
      </div>
      <div class="room-availability-picker__filter">
        <Multiselect
          v-model="Filter.room_type"
          :options="roomTypeOptions"
          label="name"
          track-by="id"
          placeholder="All room types"
          selectLabel=""
        >
          <template #singleLabel="{ option }">{{ option.name }}</template>
        </Multiselect>
      </div>
      <div class="room-availability-picker__filter room-availability-picker__filter--btn">
        <button
          type="button"
          class="room-availability-picker__btn room-availability-picker__btn--primary"
          @click="applyFilters"
        >
          Availability
        </button>
      </div>
      <div class="room-availability-picker__filter room-availability-picker__filter--search">
        <input
          v-model="search"
          type="text"
          class="room-availability-picker__search"
          placeholder="Room no., type…"
          @input="searchRooms"
        />
      </div>
    </div>

    <div v-if="typesLoaded && roomsData.length === 0" class="room-availability-picker__empty">
      <img src="@/assets/img/noresultsGif.gif" alt="" />
      <p>No rooms found for these filters.</p>
    </div>

    <div v-else class="room-availability-picker__grid">
      <article
        v-for="room in roomsData"
        :key="room.id"
        class="room-availability-card"
      >
        <div class="room-availability-card__head">
          <h5 class="room-availability-card__name">{{ room.name }}</h5>
          <span v-if="room.phone_expression" class="room-availability-card__phone">
            <i class="fa fa-phone" aria-hidden="true"></i> {{ room.phone_expression }}
          </span>
        </div>

        <div class="room-availability-card__body">
          <p class="room-availability-card__line">
            <strong>Type:</strong>
            <span
              v-for="rt in room.roomTypes"
              :key="rt.id"
              class="room-availability-card__tag"
            >
              {{ rt.name }}
            </span>
          </p>
          <p class="room-availability-card__line">
            Smoking:
            <strong>{{ room.is_smoking ? "Allowed" : "Not Allowed" }}</strong>
          </p>
          <p class="room-availability-card__line">
            Floor: <strong>{{ room.floor && room.floor.name }}</strong>
          </p>
          <p v-if="room.remarks" class="room-availability-card__remarks">
            {{ room.remarks }}
          </p>
        </div>

        <div class="room-availability-card__actions">
          <button
            v-if="isAlreadyBooked(room.id)"
            type="button"
            class="room-availability-card__btn room-availability-card__btn--reserved"
            disabled
          >
            Reserved
          </button>
          <button
            v-else-if="room.status === 'Booked'"
            type="button"
            class="room-availability-card__btn room-availability-card__btn--booked"
            @click="gotoReservation(room.reservation.id)"
          >
            <i class="fas fa-book" aria-hidden="true"></i> {{ room.status }}
          </button>
          <button
            v-else-if="room.status === 'UnBooked'"
            type="button"
            class="room-availability-card__btn room-availability-card__btn--book"
            @click="bookRoom(room.id)"
          >
            <i class="icon_cart_alt" aria-hidden="true"></i> Book Now
          </button>
          <button
            v-if="
              !isAlreadyBooked(room.id) &&
              room.status === 'Booked' &&
              isAccessFoodOrder
            "
            type="button"
            class="room-availability-card__btn room-availability-card__btn--food"
            @click="orderRoomFood(room.id, room)"
          >
            <i class="icon_mug" aria-hidden="true"></i> Food Order
          </button>
        </div>
      </article>
    </div>

    <CModal
      Text="customize"
      ref="addRoomModal"
      headerText="Room"
      :width="850"
      submitText="Book"
      :hideButton="true"
      :visibleFooter="false"
      :modalName="addRoomModalName"
    >
      <template #content>
        <Room
          v-if="addingNewRoomVisible && roomsDatasingleView"
          :item="roomsDatasingleView"
          :RoomImages="RoomImagesData"
          :FilterDate="FilterDate"
          :ReservationId="addingNewRoomReservationId"
        />
      </template>
    </CModal>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import moment from "moment";
import axios from "@/plugins/axios";
import { mapGetters, mapState } from "vuex";
import { DatePicker, CustomModal as CModal } from "@/components/base/index.js";
import Room from "@/views/SingleRoom.vue";

export default {
  name: "RoomAvailabilityPicker",
  components: {
    DatePicker,
    Multiselect,
    CModal,
    Room,
  },
  data() {
    return {
      Filter: {
        start: moment().format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD"),
        room_type: null,
      },
      StatusTypes: [
        { name: "Booked", Value: "Booked" },
        { name: "UnBooked", Value: "UnBooked" },
      ],
      status: "",
      search: "",
      RoomTypes: [],
      roomsData: [],
      roomsDatasingleView: null,
      RoomImagesData: [],
      typesLoaded: false,
      isAccessFoodOrder: false,
      currentDate: moment().format("YYYY-MM-DD"),
      endDatevalidator: moment().subtract(1, "days").format("YYYY-MM-DD"),
      addRoomModalName: "modal-add-room-inline",
      pendingModalRoomId: null,
    };
  },
  computed: {
    ...mapState("orders", [
      "FilterDate",
      "addingNewRoomVisible",
      "addingNewRoomReservationId",
    ]),
    ...mapGetters("orders", ["bookedRoomsIdList"]),
    roomTypeOptions() {
      const byId = new Map();
      const addType = (type) => {
        if (!type || type.id == null) return;
        const id = String(type.id);
        if (!byId.has(id)) {
          byId.set(id, {
            id: type.id,
            name: type.name || type.code || `Type ${type.id}`,
          });
        }
      };
      (this.RoomTypes || []).forEach(addType);
      (this.roomsData || []).forEach((room) => {
        (room.roomTypes || []).forEach(addType);
      });
      return [...byId.values()].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          numeric: true,
        }),
      );
    },
  },
  created() {
    if (this.FilterDate && this.FilterDate.start) {
      this.Filter.start = this.FilterDate.start;
      this.Filter.end = this.FilterDate.end || this.FilterDate.start;
      this.endDatevalidator = moment(this.Filter.start)
        .subtract(1, "days")
        .format("YYYY-MM-DD");
    } else {
      this.setStartDate(new Date());
    }
    this.$store.commit("orders/AddFilterDates", this.Filter);
    this.fetchRoomTypes();
    this.loadRooms();
  },
  methods: {
    async fetchRoomTypes() {
      try {
        const res = await axios.get("hms/web/room-types");
        const payload = res.data;
        this.RoomTypes = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
      } catch (err) {
        console.error("Failed to load room types:", err);
        this.RoomTypes = [];
      }
    },
    setStartDate(startDate) {
      const start = moment(new Date(startDate));
      this.Filter.start = start.format("YYYY-MM-DD");
      const end = start.clone().add(1, "days");
      this.Filter.end = end.format("YYYY-MM-DD");
      this.endDatevalidator = start.clone().subtract(1, "days").format("YYYY-MM-DD");
      this.$store.commit("orders/AddFilterDates", this.Filter);
    },
    setEndDate(startDate) {
      this.Filter.end = moment(new Date(startDate)).format("YYYY-MM-DD");
      this.$store.commit("orders/AddFilterDates", this.Filter);
    },
    applyFilters() {
      if (!this.Filter.start || !this.Filter.end) {
        this.$fire({
          title: "Error",
          text: "Date filter cannot be empty",
          type: "error",
          timer: 3000,
        });
        return;
      }
      this.$store.commit("orders/AddFilterDates", this.Filter);
      const typeId = this.Filter.room_type ? this.Filter.room_type.id : "";
      this.filterRooms(typeId);
    },
    searchRooms() {
      axios
        .get(
          `hms/web/rooms/all?start=${this.FilterDate.start}&end=${this.FilterDate.end}&query=${this.search}`
        )
        .then((res) => {
          this.roomsData = res.data || [];
          this.typesLoaded = true;
        })
        .catch(() => {});
    },
    loadRooms() {
      const typeId = this.Filter.room_type ? this.Filter.room_type.id : "";
      this.filterRooms(typeId);
    },
    filterRooms(typeId = "") {
      axios
        .get(
          `hms/web/rooms/all?start=${this.FilterDate.start}&end=${
            this.FilterDate.end
          }&status=${this.status ? this.status.name : ""}`
        )
        .then((res) => {
          let allRooms = res.data || [];
          if (typeId) {
            allRooms = allRooms.filter((room) =>
              (room.roomTypes || []).some((rt) => rt.id == typeId)
            );
          }
          this.roomsData = allRooms;
          this.isAccessFoodOrder = this.currentDate === this.Filter.start;
          this.typesLoaded = true;
        })
        .catch(() => {
          this.typesLoaded = true;
        });
    },
    isAlreadyBooked(id) {
      return this.bookedRoomsIdList.includes(id);
    },
    async fetchRoomDetails(roomId) {
      const [roomRes, imagesRes] = await Promise.all([
        axios.get(`hms/rooms/${roomId}`).catch(() => null),
        axios.get(`hms/web/rooms/${roomId}/images`).catch(() => null),
      ]);
      if (roomRes && roomRes.data) {
        this.roomsDatasingleView = roomRes.data;
      }
      if (imagesRes && imagesRes.data) {
        this.RoomImagesData = imagesRes.data.data || [];
      }
    },
    async bookRoom(roomId) {
      await this.fetchRoomDetails(roomId);

      if (this.addingNewRoomVisible) {
        this.pendingModalRoomId = roomId;
        this.addRoomModalName = `modal-addmore-${roomId}`;
        this.$nextTick(() => {
          if (this.$refs.addRoomModal) {
            this.$refs.addRoomModal.show(this.addRoomModalName);
          }
        });
        return;
      }

      const credential = {
        ...this.roomsDatasingleView,
        datefilter: { ...this.Filter },
        RoomImages: this.RoomImagesData,
      };

      if (!credential.id) {
        this.$fire({
          title: "Error",
          text: "Failed to load room details. Please try again.",
          type: "error",
          timer: 3000,
        });
        return;
      }

      this.$store.commit("orders/addOrders", credential);
      this.$emit("room-added", credential);
    },
    cancelAddingRoom() {
      this.$store.commit("orders/updateRoomVisible", false);
      this.$router.push(
        `/reservation-details/${this.addingNewRoomReservationId}`
      );
    },
    gotoReservation(reservationId) {
      this.$router.push(`/reservation-details/${reservationId}`);
    },
    orderRoomFood(roomId, room) {
      this.$store.commit("orders/AddGuestDetails", room.guest);
      this.$router.push(`/order/room/${roomId}`);
    },
  },
};
</script>

<style scoped lang="scss">
.room-availability-picker {
  --rap-control-h: 28px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  margin-top: 0;
}

.room-availability-picker__head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
}

.room-availability-picker__title {
  margin: 0;
  flex: 1;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.room-availability-picker__filters {
  --rap-control-h: 28px;
  display: grid;
  grid-template-columns:
    minmax(96px, 1fr)
    minmax(96px, 1fr)
    minmax(88px, 1fr)
    minmax(88px, 1fr)
    auto
    minmax(100px, 1.1fr);
  gap: 4px 6px;
  align-items: center;
  margin-bottom: 10px;
}

.rap-filter-label {
  margin: 0;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  line-height: 1;
  align-self: center;
  padding-bottom: 1px;
}

.rap-filter-label--action {
  visibility: hidden;
}

.room-availability-picker__filter {
  min-width: 0;
  display: flex;
  align-items: center;
  height: var(--rap-control-h);
}

.room-availability-picker__filter--btn {
  display: flex;
  align-items: center;
}

.room-availability-picker__filter--btn .room-availability-picker__btn {
  width: 100%;
  min-width: 88px;
}

.room-availability-picker__filter--search {
  grid-column: auto;
}

.room-availability-picker__filter ::v-deep .multiselect {
  min-height: var(--rap-control-h);
  font-size: 12px;
}

.room-availability-picker__filter ::v-deep .multiselect__tags {
  min-height: var(--rap-control-h) !important;
  height: var(--rap-control-h) !important;
  padding: 0 26px 0 6px !important;
  border-radius: 6px !important;
  border-color: #cbd5e1 !important;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.room-availability-picker__filter ::v-deep .multiselect__single,
.room-availability-picker__filter ::v-deep .multiselect__placeholder {
  font-size: 12px !important;
  line-height: var(--rap-control-h) !important;
  margin: 0 !important;
  padding: 0 !important;
}

.room-availability-picker__filter ::v-deep .multiselect__select {
  height: calc(var(--rap-control-h) - 2px) !important;
  width: 26px !important;
  top: 0 !important;
  padding: 0 !important;
}

.room-availability-picker__filter ::v-deep .kot-date-picker,
.room-availability-picker__filter ::v-deep .kot-date-picker .vd-wrapper {
  width: 100%;
  height: var(--rap-control-h);
}

.room-availability-picker__search {
  width: 100%;
  height: var(--rap-control-h);
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 12px;
  box-sizing: border-box;
  line-height: var(--rap-control-h);
}

.room-availability-picker__search:focus {
  outline: none;
  border-color: var(--brand-primary);
  box-shadow: 0 0 0 2px var(--brand-primary-fade-20);
}

.room-availability-picker__btn {
  height: var(--rap-control-h, 28px);
  padding: 0 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
}

.room-availability-picker__btn--primary {
  background: var(--brand-primary);
  color: #fff;
  border-color: var(--brand-primary);
}

.room-availability-picker__btn--primary:hover {
  background: var(--brand-primary-hover);
}

.room-availability-picker__btn--ghost {
  background: #fff;
  color: #64748b;
  border-color: #e2e8f0;
  height: var(--rap-control-h, 28px);
}

.room-availability-picker__btn--muted {
  background: #fff;
  color: #b91c1c;
  border-color: #fecaca;
}

.room-availability-picker__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.room-availability-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  border-top: 3px solid var(--brand-primary);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.room-availability-card__head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
}

.room-availability-card__name {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--brand-primary-dark);
}

.room-availability-card__phone {
  font-size: 12px;
  color: #64748b;
}

.room-availability-card__body {
  padding: 8px 0;
  font-size: 13px;
  color: #475569;
}

.room-availability-card__line {
  margin: 0 0 4px;
}

.room-availability-card__tag {
  display: inline-block;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 2px 6px;
  margin-right: 4px;
  font-size: 12px;
}

.room-availability-card__remarks {
  margin: 6px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

.room-availability-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
}

.room-availability-card__btn {
  flex: 1;
  min-width: 100px;
  height: 34px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.room-availability-card__btn--reserved {
  background: #e2e8f0;
  color: #64748b;
  cursor: default;
}

.room-availability-card__btn--booked {
  background: #f8fafc;
  color: var(--brand-primary-dark);
  border: 1px solid var(--brand-primary-border);
}

.room-availability-card__btn--book {
  background: #fff;
  color: var(--brand-primary-dark);
  border: 1.5px solid var(--brand-primary);
}

.room-availability-card__btn--book:hover {
  background: var(--brand-primary-muted);
}

.room-availability-card__btn--food {
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
}

.room-availability-picker__empty {
  text-align: center;
  padding: 24px 12px;
}

.room-availability-picker__empty img {
  width: 200px;
  max-width: 100%;
  opacity: 0.85;
}

.room-availability-picker__empty p {
  margin-top: 8px;
  color: #64748b;
  font-size: 14px;
}

@media (max-width: 900px) {
  .room-availability-picker__filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .rap-filter-label--action {
    display: none;
  }

  .room-availability-picker__filter--btn {
    grid-column: 1 / -1;
  }

  .room-availability-picker__filter--search {
    grid-column: 1 / -1;
  }
}
</style>
